import {
  GET_LIST_IN_REVIEW_IDP_LEARNING,
  COMPLETE_STRUCTURED_LEARNING,
  REVISE_STRUCTURED_LEARNING,
} from "@/api/certificate-review/certificate-review";
import { GET_DETAIL_STRUCTURED_LEARNING, GET_SUMMARY_IDP_LEARNING } from "@/api/idp/enrollment";
import { GET_COMPETENCY_BY_NIK } from "@/api/master-data/competency/competency";
import { GET_TRAINING_METHOD } from "@/api/master-data/training-method/training-method";
import { GET_TRAINING_TYPE } from "@/api/master-data/training-type/training-type";
import { GET_LIST_USER_BY_FILTERS } from "@/api/users/users";

const handleRequest = async (apiFunc, commit, mutation, args, emptyValue = []) => {
  try {
    const resp = await apiFunc(args);
    const { httpCode, data } = resp.data;

    if (httpCode === 200) {
      if (data?.data && data?.totalItems !== undefined) {
        commit(mutation, {
          data: data.data,
          totalItems: data.totalItems,
          page: data.page,
          limit: data.limit,
          totalPages: data.totalPages,
          approvalReport: data.approvalReport,
        });
      } else {
        commit(mutation, data);
      }
      return data;
    }

    commit(mutation, emptyValue);
    return emptyValue;
  } catch (err) {
    const resp = err?.response?.data;
    if (resp?.httpCode === 404) {
      commit(mutation, emptyValue);
    }
    throw err;
  }
};

const state = {
  idpLearnings: {
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },
  filterUserOptions: {
    company: [],
    division: [],
    department: [],
    position: [],
    employee: [],
  },
  structuredLearningDetail: null,
  summaryIdpLearning: null,
  certificateApprovalReport: {
    approved: 0,
    revision: 0,
    pending: 0,
  },
  trainingMethods: [],
  trainingTypes: [],
  competencies: [],
};

const mutations = {
  setIdpLearnings: (state, payload) => {
    state.idpLearnings = {
      data: payload.data ?? [],
      totalItems: payload.totalItems ?? 0,
      page: payload.page ?? 1,
      limit: payload.limit ?? 10,
      totalPages: payload.totalPages ?? 1,
    };
    state.certificateApprovalReport = payload.approvalReport || {
      approved: 0,
      revision: 0,
      pending: 0,
    };
  },
  setStructuredLearningDetail: (state, payload) => (state.structuredLearningDetail = payload),
  summaryIdpLearning: (state, payload) => (state.summaryIdpLearning = payload),
  setTrainingMethods: (state, payload) =>
    (state.trainingMethods = payload?.map((item) => ({ value: item.id, label: item.name })) || []),
  setTrainingTypes: (state, payload) =>
    (state.trainingTypes = payload?.map((item) => ({ value: item.id, label: item.name })) || []),
  setCompetencies: (state, payload) =>
    (state.competencies = payload?.map((item) => ({ value: item.competency_id, label: item.competency })) || []),
  resetIdpLearnings: (state) => {
    state.idpLearnings = {
      data: [],
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 1,
    };
  },
  resetSummaryIdpLearning: (state) => (state.summaryIdpLearning = null),
  setFilterUsers: (state, payload) => {
    const employee = payload?.employees?.map((item) => ({
      ...item,
      value: item.nik,
      text: `${item.fullname} - ${item.nik}`,
    }));

    state.filterUserOptions = {
      company: payload?.company || [],
      division: payload?.division || [],
      department: payload?.department || [],
      position: payload?.position || [],
      employee: employee || [],
    };
  },
};

const actions = {
  async getIdpLearnings({ commit }, query) {
    return handleRequest(GET_LIST_IN_REVIEW_IDP_LEARNING, commit, "setIdpLearnings", query, []);
  },

  async getUserByFilters({ commit }, data) {
    return handleRequest(GET_LIST_USER_BY_FILTERS, commit, "setFilterUsers", data, []);
  },

  async getStructuredLearningDetail({ commit }, { id, query }) {
    return handleRequest(GET_DETAIL_STRUCTURED_LEARNING, commit, "setStructuredLearningDetail", { id, query }, null);
  },

  async completeStructuredLearning(_, { id }) {
    return COMPLETE_STRUCTURED_LEARNING({ id });
  },

  async reviseStructuredLearning(_, { id, note }) {
    return REVISE_STRUCTURED_LEARNING({ id, note });
  },

  async getSummaryIdpLearning({ commit }, query) {
    const resp = await handleRequest(GET_SUMMARY_IDP_LEARNING, commit, "summaryIdpLearning", query, null);
    return resp;
  },

  async getTrainingMethods({ commit }, id) {
    return handleRequest(GET_TRAINING_METHOD, commit, "setTrainingMethods", id, null);
  },

  async getTrainingTypes({ commit }, id) {
    return handleRequest(GET_TRAINING_TYPE, commit, "setTrainingTypes", id, null);
  },

  async getCompetencies({ commit }, id) {
    return handleRequest(GET_COMPETENCY_BY_NIK, commit, "setCompetencies", id, null);
  },

  resetIdpLearnings({ commit }) {
    commit("resetIdpLearnings");
    commit("resetSummaryIdpLearning");
  },
};

const getters = {
  idpLearnings: (state) => state.idpLearnings,
  structuredLearningDetail: (state) => state.structuredLearningDetail,
  certificateApprovalReport: (state) => state.certificateApprovalReport,
  summaryIdpLearning: (state) => state.summaryIdpLearning,
  trainingMethods: (state) => state.trainingMethods,
  trainingTypes: (state) => state.trainingTypes,
  competencies: (state) => state.competencies,
  filterUserOptions: (state) => state.filterUserOptions,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
